import { useContext, useEffect, useRef, useState } from "react";
import { StyleContext } from "../providers/StyleContextProvider";
import { AuthContext } from "../providers/AuthContextProvider";
import { useMediaQuery } from "react-responsive";
import { RecruiterMenu } from "../components/Shared/RecruiterMenu";
import { Job } from "../models/Job";
import { VacancySearchCriteria } from "../models/VacancySearchCriteria";
import { createSearchParams, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import useErrorPage from "../hooks/useErrorPage";
import { getOrganizationDetails, getJobVacancies, downloadCVLessExcel, addToExcelCvLessVacancy, duplicateJobVacancies, archiveJobVacancies, getJobVacancyCounts } from "../services/RecruiterService";
import { VacancyCounts } from "../models/VacancyCounts";
import { Button } from "../components/Shared/Buttons/Button";
import { ReactComponent as Plus } from '../assets/svg/plus.svg';
import { LoggedRecruiter } from "../models/LoggedRecruiter";
import { Footer } from "../components/Shared/Footer";
import { SingleSelect } from "../components/Shared/SingleSelect";
import { Option, SelectInputs } from "../models/SelectInputs";
import { ReactComponent as Excel } from "../assets/svg/excel.svg";
import { ReactComponent as Edit } from "../assets/svg/edit.svg";
import { ReactComponent as Duplicate } from "../assets/svg/duplicate.svg";
import { ReactComponent as EyeClose } from "../assets/svg/eye-close.svg";
import { ReactComponent as EyeOpen } from "../assets/svg/eye-open.svg";
import { ReactComponent as BumpUp } from "../assets/svg/bump-up.svg";
import { ReactComponent as CloseCircle } from "../assets/svg/close-circle.svg";
import { Pagination } from "../components/Shared/Pagination";
import { formatDateWithSuperscript } from "../helpers/Common";
import { CommonTexts as Texts} from "../helpers/LayoutTexts";
import { VacancyModel } from "../models/VacancyDetails";
import { Helmet } from "react-helmet-async";
import { PopUpWindow } from "../components/Shared/PopUpWindow";
import { useAlert } from "../hooks/useAlert";
import { VacancyCountLoader } from "../components/Loaders/VacancyCountLoader";
import { VacancyListLoader } from "../components/Loaders/VacancyListLoader";
import { FeedbackCard } from '../components/Shared/FeedbackCard';
import { ReactComponent as CheckMark } from '../assets/svg/checkmark.svg';
import { Sector } from '../models/Sector';
import { SearchFilterOptions } from "../models/SearchFilterOptions";
import { getSearchFilterOptions } from '../services/JobService';
import { ReactComponent as Icon } from '../assets/svg/drop_down.svg';
import { JobType } from "../models/JobType";
import { ProfileEntrance } from "../components/Home/ProfileEntrance";
import { RecruiterFilterPanel } from "../components/Shared/RecruiterFilterPanel";
import { ReactComponent as Search } from '../assets/svg/search.svg';
import { Invoice } from "../models/Invoice";
import { ReactComponent as WarningOrange } from "../assets/svg/warning-orange.svg";
import { RecruiterQuotaDetails } from "../models/RecruiterQuotaDetails";
import { UserType } from "../enums/UserType";
import { CheckoutTypes } from "../enums/CheckoutTypes";
import { ReactComponent as Location } from "../assets/svg/pin_icon.svg";
import { ReactComponent as Filter } from "../assets/svg/filter.svg";

export const RecruiterHomePage: React.FC = () => {
    const location = useLocation();    
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();  
    const styleContext = useContext(StyleContext);
    const authContext = useContext(AuthContext);
    const recruiter: LoggedRecruiter | null = authContext.getLoggedRecruiter();
    const [styles, setStyles] = useState<any>(styleContext.getComponentStyle("recruiterHomePage"));    
    const [loading, setLoading] = useState<boolean>(true);
    const [countsLoading, setCountsLoading] = useState<boolean>(true);
    const pageSize: number = searchParams.get("pageSize") === null ? 20 : +searchParams.get("pageSize")!;
    const page: number = searchParams.get("page") === null ? 1 : +searchParams.get("page")!;
    const status: string = searchParams.get("status") === null ? "LIVE" : searchParams.get("status")!; 
    const keyword: string = searchParams.get("keyword") === null ? "" : searchParams.get("keyword")!;
    const organizationId: number = searchParams.get("orgId") === null ? 0 : +searchParams.get("orgId")!;
    const applicationType: string = searchParams.get("appType") === null ? "ALL" : searchParams.get("appType")!;
    const [sortBy, setSortBy] = useState<string>("LastUpdatedDate DESC");      
    const [totalPages, setTotalPages] = useState<number>();     
    const [vacancyCounts, setVacancyCounts]  = useState<VacancyCounts>();
    const [vacancies, setVacancies] = useState<Job[]>([]);
    const [hasSubOrganizations, setHasSubOrganizations] = useState<boolean>(false);
    const [orgSelectInputs, setOrgSelectInputs] = useState<SelectInputs>();
    const [isSubOrganization, setIsSubOrganization] = useState<boolean>(false);
    const isMobile = useMediaQuery({ query: "(max-width: 786px)" });    
    const [openVerifyDialog, setOpenVerifyDialog] = useState<boolean>(!recruiter?.emailVerified);
    const isMountedRef = useRef<boolean>(true);
    const retryCountRef = useRef<number>(1);
    const maxRetries = 3;   
    const [displayDownload, setDisplayDownload] = useState<boolean>(false);
    const [downloadFromDate, setDownloadFromDate] = useState<string>("");
    const [downloadToDate, setDownloadToDate] = useState<string>("");
    const [hoveredIndex, setHoveredIndex] = useState<number>(-1);
    const [openMessageDialog, setOpenMessageDialog] = useState<boolean>(false);
    const [messageDialogContent, setMessageDialogContent] = useState<any>(null);
    const {Alert, openAlert} = useAlert();
    const [toErrorPage] = useErrorPage();
    const downloadRef = useRef<HTMLDivElement>(null);
    const titleRefs = useRef<(HTMLDivElement | null)[]>([]);
    const [sectors, setSectors] = useState<Sector[]>([]);
    const [jobTypes, setJobTypes] = useState<JobType[]>([]);
    const [selectedVacancies, setSelectedVacancies] = useState<{ [key: number]: boolean }>({});
    const [openMenuJobId, setOpenMenuJobId] = useState<number | null>(null);
    const [isFilterPanelOpen, setIsFilterPanelOpen] = useState<boolean>(false);
    const filterPanelRef = useRef<HTMLDivElement>(null);
    const sectorIds: string = searchParams.get("sectorIds") === null ? "" : searchParams.get("sectorIds")!;
    const jobTypeIds: string = searchParams.get("jobTypeIds") === null ? "" : searchParams.get("jobTypeIds")!;
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [searchFocused, setSearchFocused] = useState<boolean>(false);
    const [openInvoiceDialog, setOpenInvoiceDialog] = useState<boolean>(true);
    const [pendingInvoice, setPendingInvoice] = useState<Invoice>();
    const [openQuotaDialog, setOpenQuotaDialog] = useState<boolean>(false);
    const [quotaMessageContent, setQuotaMessageContent] = useState<any>();
    const subOrganizationIdsRef = useRef<string>("");
    const isOrganizationLoadedRef = useRef<boolean>(false);
    const VACANCY_THRESHOLD = 15;
    const hasExcessVacancies = (vacancies[0]?.recordCount ?? 0) > VACANCY_THRESHOLD;    

    const sortBySelectInputs: SelectInputs = {
        default: "Recently updated",
        options: [
            {"value": "CreatedDate DESC", "option": "Recently added"},
            {"value": "CreatedDate ASC", "option": "Formerly added"},
            {"value": "LastUpdatedDate DESC", "option": "Recently updated"}
        ]
    }

    const perPageSelectInputs: SelectInputs = {
        default: pageSize.toString(),
        options: [            
            {"value": 15, "option": "15"},
            {"value": 20, "option": "20"},
            {"value": 30, "option": "30"},
            {"value": 60, "option": "60"}
        ]
    }

    useEffect(() => {
        if(recruiter === null) {   
            const params = { callbackUrl: location.pathname + location.search, message: "Please login or register to access the vacancy manager." };
            navigate({
                pathname: '/recruiter/account/login',
                search: `?${createSearchParams(params)}`
            });            
        }   
        else {            
            getDetails();
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        const close = () => setOpenMenuJobId(null);
        document.addEventListener("click", close);
        return () => document.removeEventListener("click", close);
    }, []);

    useEffect(() => {
        window.scrollTo({top: 0, behavior: 'smooth'});                   
        if (!isOrganizationLoadedRef.current) return;                   
        getVacancyList();        
    }, [page, pageSize, status, keyword, sortBy, applicationType, organizationId, sectorIds, jobTypeIds]);

    useEffect(() => {
        setStyles(styleContext.getComponentStyle("recruiterHomePage"));
    }, [isMobile]);

    useEffect(() => {
        if (!isOrganizationLoadedRef.current) return;
        getVacancyCounts();
    }, [organizationId]);

    const handleTitleScroll = (index: number) => {
        const maxWidthPercent = isMobile ? 58 : 24;
        const titleElement = titleRefs.current[index];
        if (!titleElement) {
            return false;
        }
        else {
            const titleWidth = titleElement.scrollWidth;
            const maxWidth = maxWidthPercent ? (maxWidthPercent / 100) * window.innerWidth : parseFloat(window.getComputedStyle(titleElement).maxWidth);
            return titleWidth > maxWidth;
        }
    };

    const handleOpenVerifyDialog = () => {
        setOpenVerifyDialog(false);
        if (quotaMessageContent) {
            setOpenQuotaDialog(true);
        }
    }
    
    async function getDetails() {        
        await getOrganizationDetails(recruiter!.recruiterId, recruiter!.organizationId, recruiter!.isOrganizationAdmin, recruiter!.token)
        .then((vacancyDetails: VacancyModel) => {            
            if(vacancyDetails.hasSubOrganizations) {
                const orgOptions = new Array<Option>();                 
                orgOptions.push({
                    value: 0,
                    option: "All"
                });                 
                vacancyDetails.subOrganizations.forEach((organization) => {                           
                    let option: Option = {
                        value: organization.organizationId,
                        option: organization.organizationName
                    }           
                    orgOptions.push(option);
                });
                const tempSelectInputs = {
                    default: orgOptions.find(o => o.value === organizationId)?.option ?? "All",
                    options: orgOptions
                }
                setOrgSelectInputs(tempSelectInputs);
                setHasSubOrganizations(vacancyDetails.hasSubOrganizations);
                subOrganizationIdsRef.current = vacancyDetails.subOrganizations.map((x) => x.organizationId).join(',');
            }                        
            setIsSubOrganization(vacancyDetails.isSubOrganization);
        })    
        .catch((error) => {            
            toErrorPage(error, UserType.Recruiter);                
        })
        .finally(() => {
            isOrganizationLoadedRef.current = true;
            getVacancyList();
            getVacancyCounts();
            getFilterOptions();
        });       
    }

    async function getVacancyList() {        
        setLoading(true);
        let searchCriteria: VacancySearchCriteria = {
            page: page,
	        pageSize: pageSize,
	        recruiterId: recruiter!.recruiterId,
	        organizationId: organizationId === 0 ? recruiter!.organizationId : organizationId,
            subOrganizationIds: (organizationId === 0 && subOrganizationIdsRef.current !== "") ? subOrganizationIdsRef.current : '',
	        isOrganizationAdmin: recruiter!.isOrganizationAdmin,		
	        jobStatus: status, // (live, archived, pending)
	        sortVal: sortBy, // (CreatedDate DESC)
            applicationType: applicationType,
	        searchKeyword: keyword,
            sectors : sectorIds,
            jobTypes : jobTypeIds
        }
        await getJobVacancies(searchCriteria, recruiter!.token)
        .then((vacancyDetails: VacancyModel) => {
            const vacancyList: Job[] = vacancyDetails.vacancies;
            setVacancies(vacancyList);             
            if(vacancyList.length > 0) {
                let totalPageCount = Math.ceil(vacancyList[0].recordCount / pageSize);            
                setTotalPages(totalPageCount);                           
            } else {
                setTotalPages(1);                
            }
            setLoading(false);
        })    
        .catch((error) => {                  
            if(isMountedRef.current) {                   
                if (retryCountRef.current < maxRetries) {
                    // Retry the API call after 1 second
                    retryCountRef.current = retryCountRef.current + 1;
                    setTimeout(getVacancyList, 1000);
                } 
                else {
                    // Redirect to error page after max retries
                    toErrorPage(error, UserType.Recruiter);
                }  
            }
        });       
    }

    async function getVacancyCounts() {
        setCountsLoading(true);
        let searchCriteria: VacancySearchCriteria = {
            recruiterId: recruiter!.recruiterId,
            organizationId: organizationId === 0 ? recruiter!.organizationId : organizationId,
            subOrganizationIds: (organizationId === 0 && subOrganizationIdsRef.current !== "") ? subOrganizationIdsRef.current : '',
            isOrganizationAdmin: recruiter!.isOrganizationAdmin
        }
        await getJobVacancyCounts(searchCriteria, recruiter!.token)
        .then((counts: VacancyCounts) => {
            setVacancyCounts(counts);
            setCountsLoading(false);
        })    
        .catch((error) => {                  
            if(isMountedRef.current) {                   
                if (retryCountRef.current < maxRetries) {
                    retryCountRef.current = retryCountRef.current + 1;
                    setTimeout(getVacancyCounts, 1000);
                } 
                else {
                    // toErrorPage(error, UserType.Recruiter);
                }  
            }
        });       
    }

    const setOrganization = (selectedOrgId: number) => {
        searchParams.set("page", "1");
        searchParams.set("orgId", selectedOrgId.toString());
        const newUrl = `?${searchParams}`;
        navigate(newUrl);
    }

    const setStatus = (selectedStatus: string) => {
        setSelectedVacancies({});
        searchParams.set("page", "1");
        searchParams.set("status", selectedStatus);
        const newUrl = `?${searchParams}`;
        navigate(newUrl);
    }

    const setPageSize = (selectedPageSize: number) => {
        searchParams.set("page", "1");
        searchParams.set("pageSize", selectedPageSize.toString());
        const newUrl = `?${searchParams}`;
        navigate(newUrl);
    }
    
    const setApplicationType = (selectedType: string) => {
        searchParams.set("page", "1");
        searchParams.set("appType", selectedType);
        const newUrl = `?${searchParams}`;
        navigate(newUrl);
    }

    const handlePagination = (selectedPage: number) => {
        searchParams.set("page", selectedPage.toString());
        const newUrl = `?${searchParams}`;
        window.scrollTo({top: 0, behavior: 'smooth'});
        navigate(newUrl);        
    } 

    const handleSearchSubmit = (searchTerm: string) => {
        searchParams.set("keyword", searchTerm);
        searchParams.set("page", "1");
        const newUrl = `?${searchParams}`;
        navigate(newUrl);       
    } 

    const handleDisplayDownload = () => {
        setDisplayDownload(!displayDownload);
    }

    const handleClickOutside = (event: MouseEvent) => {
        if (downloadRef.current && !downloadRef.current.contains(event.target as Node)) {
            setDisplayDownload(false);
        }
    };

    async function downloadCVLessExcelSheet() {
        if (new Date(downloadFromDate) > new Date(downloadToDate)) {
            openAlert("error", "Please enter a valid date!");
            return;
        }

        setDisplayDownload(false);
        await downloadCVLessExcel(recruiter!.recruiterId, organizationId, downloadFromDate!, downloadToDate!, recruiter!.token)
        .then((response: any) => {
           
        })
        .catch((error) => {
            toErrorPage(error, UserType.Recruiter);            
        });
    }
    
    const editVacancy = (jobId: number) => {
        navigate(`/recruiter/vacancy/edit/${jobId}?orgId=${organizationId}`);
    }

    const displayDuplicateWarning = (jobId: number, jobTitle: string) => {
        let content: any = <>
            <h1 style={styles.dialog.title}>{"Are you sure?"}</h1>
            <p style={styles.dialog.message}>{`Do you want to duplicate ${jobTitle} vacancy?`}</p>
            <div className="center-content">
                <Button style={{...styles.dialog.button, ...styles.dialog.button.blue}} onClick={closeMessageDialog}>{"NO"}</Button>                
                <Button style={{...styles.dialog.button, ...styles.dialog.button.red}} onClick={() => duplicateVacancy(jobId)}>{"YES"}</Button>
            </div>
        </>
        setMessageDialogContent(content);        
        setOpenMessageDialog(true);
    }
    
    const previewVacancy = (jobId: number) => {
        window.open(`/recruiter/preview/${jobId}`, "_blank");
    }

    const handlePendingInvoice = (invoice?: Invoice) => {
        setPendingInvoice(invoice);
    }

    const closeInvoiceDialog = () => {
        setOpenInvoiceDialog(false); 
    }

    const handlePayInvoiceClick = () => {
        const refId = pendingInvoice?.refId;
        navigate(`/recruiter/invoice/pay/${refId}`, { replace: true });
    }

    const displayArchiveWarning = (jobId: number, jobTitle: string) => {
        let content: any = <>
            <h1 style={styles.dialog.title}>{"Are you sure?"}</h1>
            <p style={styles.dialog.message}>{`Do you want to unpublish ${jobTitle} vacancy?`}</p>
            <div className="center-content">
                <Button style={{...styles.dialog.button, ...styles.dialog.button.blue}} onClick={closeMessageDialog}>{"NO"}</Button>                
                <Button style={{...styles.dialog.button, ...styles.dialog.button.red}} onClick={() => archiveVacancy(jobId)}>{"YES"}</Button>
            </div>
        </>
        setMessageDialogContent(content);        
        setOpenMessageDialog(true);        
    }

    const navigateToApplicationStatus =(jobId: number, jobTitle: string) => {
        navigate(`applications/${jobId}?title=${encodeURIComponent(jobTitle)}`, {
            state: { vacancyListParams: searchParams.toString() }
        });
    }

    async function duplicateVacancy(jobId: number) {
        setOpenMessageDialog(false);        
        setLoading(true);      
        await duplicateJobVacancies(jobId.toString(), recruiter!.recruiterId, recruiter!.isSystemAdmin, recruiter!.token)
        .then((result) => {                            
            if(result.length > 0 && result[0].success) {
                openAlert("success", "Vacancy duplicated successfully. Your job is being processed by the XpressJobs team and will be posted into the site shortly.");
                searchParams.set("status", "PENDING");
                const newUrl = `?${searchParams}`;
                navigate(newUrl);     
                getVacancyCounts();
            } else {
                setLoading(false);
                openAlert("error", "Error occured during the process. Please try again!");
            }
        })    
        .catch((error) => {            
            toErrorPage(error, UserType.Recruiter);
        });        
    }
    
    async function archiveVacancy(jobId: number) {
        setOpenMessageDialog(false);        
        setLoading(true);    
        await archiveJobVacancies(jobId.toString(), recruiter!.recruiterId, recruiter!.isSystemAdmin, recruiter!.token)
        .then((result) => {                            
            if(result.length > 0 && result[0].success) {
                openAlert("success", "Vacancy unpublished successfully.");
                getVacancyList();                           
                getVacancyCounts();
            } else {
                setLoading(false);
                openAlert("error", "Error occured during the process. Please try again!");
            }
        })    
        .catch((error) => {            
            toErrorPage(error, UserType.Recruiter);
        });        
    }

    const closeMessageDialog = () => {
        setOpenMessageDialog(false); 
    }

    // const displayMultipleDuplicateWarning = (jobIds: number[]) => {
    //     let content: any = <>
    //         <h1 style={styles.dialog.title}>{"Are you sure?"}</h1>
    //         <p style={styles.dialog.message}>{`Do you want to duplicate vacancies?`}</p>
    //         <div className="center-content" style={styles.deleteButtons}>
    //             <Button style={{...styles.dialog.button, ...styles.dialog.button.blue}} onClick={closeMessageDialog}>{"NO"}</Button>                
    //             <Button style={{...styles.dialog.button, ...styles.dialog.button.red}} onClick={() => duplicateVacancies(jobIds)}>{"YES"}</Button>
    //         </div>
    //     </>
    //     setMessageDialogContent(content);        
    //     setOpenMessageDialog(true);
    // }

    const displayMultipleArchiveWarning = (jobIds: number[]) => {  
        let content: any = <>
            <h1 style={styles.dialog.title}>{"Are you sure?"}</h1>
            <p style={styles.dialog.message}>{`Do you want to unpublish vacancies?`}</p>
            <div className="center-content">
                <Button style={{...styles.dialog.button, ...styles.dialog.button.blue}} onClick={closeMessageDialog}>{"NO"}</Button>                
                <Button style={{...styles.dialog.button, ...styles.dialog.button.red}} onClick={() => archiveVacancies(jobIds)}>{"YES"}</Button>
            </div>
        </>
        setMessageDialogContent(content);        
        setOpenMessageDialog(true);
    }

    const toggleCheckbox = (e: React.MouseEvent, vacancy: Job) => {
        e.stopPropagation();
        setSelectedVacancies((prevState) => {
            const newState = { ...prevState };
            if (newState[vacancy.jobId] !== undefined) {
                delete newState[vacancy.jobId];
            } else {
                newState[vacancy.jobId] = vacancy.isPreApplicationFormEnabled;
            }
            return newState; 
        });
    };

    const addToExcelCvLess = async (e: React.MouseEvent, jobId: number) => {
        e.stopPropagation();
        await addToExcelCvLessVacancy(jobId, recruiter!.recruiterId, recruiter!.token)
        .then(() => {  
            
        })    
        .catch((error) => {            
            toErrorPage(error, UserType.Recruiter);
        }); 
    };

    const selectSector = (sectorId: number) => {
        let tempSectors = [...sectors];
        let selectedSector = tempSectors.find(x => x.sectorId === sectorId);
        if (selectedSector) {
            selectedSector.isSelected = !selectedSector.isSelected;
            setSectors(tempSectors);
            const selectedIds = tempSectors.filter(sector => sector.isSelected).map(sector => sector.sectorId).join(',');
            if (selectedIds) {
                searchParams.set("sectorIds", selectedIds);
            } else {
                searchParams.delete("sectorIds");
            }
            searchParams.set("page", "1");
            const newUrl = `?${searchParams}`;
            navigate(newUrl);
        }
    }

    const selectJobType = (jobTypeId: number) => {
        let tempJobTypes = [...jobTypes];
        const selectedJobType = tempJobTypes.find(x => x.jobTypeId === jobTypeId);
        if (selectedJobType) {
            selectedJobType.isSelected = !selectedJobType.isSelected;
            setJobTypes(tempJobTypes);
            const selectedIds = tempJobTypes.filter(jobType => jobType.isSelected).map(jobType => jobType.jobTypeId).join(',');
            if(selectedIds === "") {
                searchParams.delete("jobTypeIds");
            }
            else{
                searchParams.set("jobTypeIds", selectedIds);
            }
            searchParams.set("page", "1");
            const newUrl = `?${searchParams}`;
            navigate(newUrl);
        }
    }

    const handleToggleQuotaDialog = () => {
        setOpenQuotaDialog(prev => !prev);
    };

    const toPurchase = () => {
        navigate('/recruiter/vacancy/checkout', {
            state: {
                checkoutType: CheckoutTypes.CVQuota
            }
        })
    }

    const handleQuotaDetailsLoaded = (quotaDetails: RecruiterQuotaDetails) => {
        if (quotaDetails?.quotaType != null && !quotaDetails.isSubOrganization && (quotaDetails?.hasExpired || quotaDetails?.hasEmpty)) {
            if (quotaDetails?.isTemp) {
                let content = <>
                    <h4 style={styles.quotaDialog.title}>{"You don't have an active CV package"}</h4>
                    <div style={styles.quotaDialog.buttonWrapper}>
                        <Button style={styles.quotaDialog.purchaseButton} onClick={toPurchase}>{"Purchase New CV Package"}</Button>
                    </div>
                </>;
                setQuotaMessageContent(content);
                if (!openVerifyDialog) {
                    setOpenQuotaDialog(true);
                }
            }
            else {
                if (quotaDetails?.hasExpired) {
                    let content = <>
                        <div style={styles.quotaDialog.alertWrapper}>
                            <div style={styles.quotaDialog.expiredBadge}>{"Monthly CV Quota Expired!"}</div>
                        </div>
                        <table style={styles.quotaDialog.table}>
                            <tbody>
                                <tr>
                                    <td style={styles.quotaDialog.tableCell}>
                                        <div style={styles.quotaDialog.invalidLabel}>{"Expired Date"}</div>
                                    </td>
                                    <td style={styles.quotaDialog.tableCell}>
                                        <div style={styles.quotaDialog.invalidValue}>
                                            {quotaDetails?.expireDate ? new Date(quotaDetails.expireDate).toISOString().split('T')[0] : ''}
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <div style={styles.quotaDialog.buttonWrapper}>
                            <Button style={styles.quotaDialog.topUpButton} onClick={toPurchase}>{"Top-Up Now!"}</Button>
                        </div>
                    </>;
                    setQuotaMessageContent(content);
                    if (!openVerifyDialog) {
                        setOpenQuotaDialog(true);
                    }
                }
                else if (quotaDetails?.hasEmpty) {
                    let content = <>
                        <div style={styles.quotaDialog.alertWrapper}>
                            <div style={styles.quotaDialog.expiredBadge}>{"Empty CV Quota!"}</div>
                        </div>
                        <div style={styles.quotaDialog.buttonWrapper}>
                            <Button style={styles.quotaDialog.topUpButton} onClick={toPurchase}>{"Top-Up Now!"}</Button>
                        </div>
                    </>;
                    setQuotaMessageContent(content);
                    if (!openVerifyDialog) {
                        setOpenQuotaDialog(true);
                    }
                }
            }
        }
    };

    // async function duplicateVacancies(jobIds: number[]) {
    //     setOpenMessageDialog(false);
    //     setLoading(true);
    //     const jobIdsString = jobIds.join(',');
    //     await duplicateJobVacancies(jobIdsString, recruiter!.recruiterId, recruiter!.isSystemAdmin, recruiter!.token)
    //     .then((results: any[]) => {  
    //         const successCount = results.filter(r => r.success).length;
    //         if(successCount > 0) {
    //             openAlert("success", `${successCount} Vacancies duplicated successfully. Your jobs are being processed by the XpressJobs team and will be posted into the site shortly.`);
    //             searchParams.set("status", "PENDING");
    //             const newUrl = `?${searchParams}`;
    //             navigate(newUrl);  
    //             setSelectedVacancies({});               
    //             getVacancyCounts();
    //         } else {
    //             setLoading(false);
    //             openAlert("error", "Error occured during the process. Please try again!");
    //         }
    //     })    
    //     .catch((error) => {            
    //         toErrorPage(error, UserType.Recruiter);
    //     }); 
    // }

    async function archiveVacancies(jobIds: number[]) {
        setOpenMessageDialog(false);
        setLoading(true);
        const jobIdsString = jobIds.join(',');
        await archiveJobVacancies(jobIdsString, recruiter!.recruiterId, recruiter!.isSystemAdmin, recruiter!.token)
        .then((results: any[]) => {   
            const successCount = results.filter(r => r.success).length;
            if(successCount > 0) {
                openAlert("success", `${successCount} Vacancies unpublished successfully.`);
                getVacancyList(); 
                getVacancyCounts();
                setSelectedVacancies({});               
            } else {
                setLoading(false);
                openAlert("error", "Error occured during the process. Please try again!");
            }
        })    
        .catch((error) => {            
            toErrorPage(error, UserType.Recruiter);
        }); 
    }

    async function getFilterOptions() {
        const byCVLess = false;
        const byWalkIn = false;
        const selectedSectorIds = sectorIds !== "" ? sectorIds.split(",").map(Number) : [];
        const selectedJobTypeIds = jobTypeIds !== "" ? jobTypeIds.split(",").map(Number) : [];
        const organizationIds = subOrganizationIdsRef.current !== ""
            ? subOrganizationIdsRef.current
            : recruiter!.organizationId.toString();
        await getSearchFilterOptions(byCVLess, byWalkIn, organizationIds)
        .then((filterOptions: SearchFilterOptions) => {
            filterOptions.sectors.forEach((sector) => {
                let isSelected: boolean = selectedSectorIds.includes(sector.sectorId);
                sector.isSelected = isSelected;
            });
            filterOptions.jobTypes.forEach((jobType) => {
                let isSelected: boolean = selectedJobTypeIds.includes(jobType.jobTypeId);
                jobType.isSelected = isSelected;
            });
            setSectors(filterOptions.sectors);
            setJobTypes(filterOptions.jobTypes);
        })
        .catch((error) => {
            if(isMountedRef.current) {         
                if (retryCountRef.current < maxRetries) {
                    // Retry the API call after 1 second
                    retryCountRef.current = retryCountRef.current + 1;
                    setTimeout(getFilterOptions, 1000);
                } 
                else {
                    // Redirect to error page after max retries
                    toErrorPage(error, UserType.Recruiter);
                }  
            }
        });
    }

    const getActiveFilterCount = (): number => {
        let count = 0;
        if (sectorIds !== "") count++;
        if (jobTypeIds !== "") count++;
        if (applicationType !== "ALL") count++;
        if (organizationId !== 0) count++;
        return count;
    };

    const clearAllFilters = () => {
        searchParams.delete("sectorIds");
        searchParams.delete("jobTypeIds");
        searchParams.delete("orgId");
        searchParams.delete("appType");
        searchParams.set("page", "1");
        navigate(`?${searchParams}`);
        setIsFilterPanelOpen(false);
        setSectors(prev => prev.map(s => ({ ...s, isSelected: false })));
        setJobTypes(prev => prev.map(j => ({ ...j, isSelected: false })));
        setOrgSelectInputs(prev => prev ? { ...prev, default: "All" } : prev);
    };

    const handleBulkExport = async () => {
        const jobIds = Object.entries(selectedVacancies)
            .filter(([_, isPreApplicationFormEnabled]) => isPreApplicationFormEnabled === true)
            .map(([jobId]) => +jobId);
        if (jobIds.length === 0) {
            openAlert("warning", "Please select at least one job to export.");
            return;
        }
        for (const jobId of jobIds) {
            await addToExcelCvLessVacancy(jobId, recruiter!.recruiterId, recruiter!.token).catch(() => {});
        }
        openAlert("success", `${jobIds.length} job(s) added to CV-less export.`);
    };

    const toVacancyPosting = () => {
        navigate(`/recruiter/vacancy/addedit?orgId=${organizationId}`);
    }
    
    const toPromoteAd = (event: React.MouseEvent, jobId: number) => {
        event.stopPropagation();
        navigate(`/recruiter/vacancy/promotead/${jobId}`);
    }

    const getStatusText = (vacancy: Job): string => {
        if (vacancy.archived) return "Archived";
        if (vacancy.confirmationPending) return "Pending";
        if (vacancy.published) return "Live";
        return "Draft";
    };

    const hasExportableSelectedVacancy = (): boolean => {
        return Object.values(selectedVacancies).some(isPreApplicationFormEnabled => isPreApplicationFormEnabled === true)
    };

    const formatLocations = (locations: string): string => {
        if (!locations) return "";
        const locationList = locations.split(";").map(loc => loc.trim()).filter(Boolean);
        if (locationList.length <= 2) {
            return locationList.join(", ");
        }
        return `${locationList[0]}, ${locationList[1]}, etc.`;
    };

    return (
        <>
            <Helmet><title>Vacancy Manager - XpressJobs</title></Helmet>
            {(!isMobile && pendingInvoice) && <div style={styles.invoiceReminder}>
                <div style={styles.invoiceReminder.message} onClick={handlePayInvoiceClick}>
                    {"You have a pending invoice payment. Click here to pay now."}
                </div>
            </div>}
            <div style={{...styles.mainWrapper, ...(pendingInvoice && styles.mainWrapper.hidePaddingTop)}}>
                <div style={styles.leftWrapper}>
                    <RecruiterMenu hasPendingInvoice={handlePendingInvoice} onQuotaDetailsLoaded={handleQuotaDetailsLoaded}>
                        <Button style={styles.newJob} onClick={toVacancyPosting}>
                            {"Create a New Job"}
                            <span style={styles.newJob.iconWrapper}><Plus style={styles.newJob.icon}/></span>
                        </Button>
                    </RecruiterMenu>
                </div>
                <div style={styles.rightWrapper}>
                    <div style={styles.searchWrapper}>
                        <div style={{...styles.searchBar, ...(searchFocused && styles.searchBar.focused)}}>
                            <Search style={styles.searchBar.icon} onClick={() => handleSearchSubmit(searchTerm)}/>
                            <input
                                style={styles.searchBar.input}
                                placeholder={"Search jobs"}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit(searchTerm)}
                                onFocus={() => setSearchFocused(true)}
                                onBlur={() => setSearchFocused(false)}
                            />
                        </div>
                        <div style={styles.profileWrapper}><ProfileEntrance recruiter={recruiter} /></div>
                    </div>
                    {(isMobile && pendingInvoice) && <div style={styles.invoiceReminder}>
                        <p style={styles.invoiceReminder.message} onClick={handlePayInvoiceClick}>
                            {"You have a pending invoice payment. Click here to pay now."}
                        </p>
                    </div>}
                    <div style={styles.statusSection}>
                        <div style={styles.header}>
                            <div style={styles.header.titleGroup}>
                                <span style={styles.header.title}>{"Jobs"}</span>
                            </div>
                            <Button style={styles.header.postJob} onClick={toVacancyPosting}>
                                <Plus style={styles.header.postJobIcon}/>
                                {"Post a job"}
                            </Button>
                        </div>
                        <div style={styles.tabs} className="disableScrollBar">
                            {countsLoading ? <VacancyCountLoader/> : <>
                                <div style={{...styles.tab, ...(status === "ALL" && styles.tab.active)}} onClick={() => setStatus("ALL")}>
                                    {"All"}<span style={styles.tab.count}>{vacancyCounts!.all}</span>
                                </div>
                                <div style={{...styles.tab, ...(status === "LIVE" && styles.tab.active)}} onClick={() => setStatus("LIVE")}>
                                    {"Live"}<span style={styles.tab.count}>{vacancyCounts!.live}</span>
                                </div>
                                <div style={{...styles.tab, ...(status === "ARCHIVE" && styles.tab.active)}} onClick={() => setStatus("ARCHIVE")}>
                                    {"Archive"}<span style={styles.tab.count}>{vacancyCounts!.archived}</span>
                                </div>
                                <div style={{...styles.tab, ...(status === "PENDING" && styles.tab.active)}} onClick={() => setStatus("PENDING")}>
                                    {"Pending"}<span style={styles.tab.count}>{vacancyCounts!.pending}</span>
                                </div>
                            </>}
                        </div>
                    </div>
                    <div style={styles.selectWrapper} ref={filterPanelRef}>
                        {/* Sort + Filters button + Export CV-less */}
                        <div style={styles.toolbarRow}>
                            <SingleSelect styles={styles.sortSelect} selectInputs={sortBySelectInputs} getSelected={setSortBy} />
                            <div style={styles.filterButtonWrapper}>
                                <div style={styles.filterButton} onClick={() => setIsFilterPanelOpen(!isFilterPanelOpen)}>
                                    <Filter style={styles.filterIcon}/>
                                    {"Filters"}
                                    {getActiveFilterCount() > 0 && <span style={styles.filterBadge}>{getActiveFilterCount()}</span>}
                                    <Icon style={{...styles.buttonIcon, ...(isFilterPanelOpen && styles.buttonIcon.hover)}}/>
                                </div>
                            </div>
                            <div style={styles.downloadExcel.wrapper} ref={downloadRef}>
                                <div style={styles.downloadExcel.button} onClick={handleDisplayDownload}>
                                    <Excel style={styles.downloadExcel.icon} />
                                    {"Export CV-Less"}
                                </div>
                                <div style={{...styles.downloadExcel.box, ...(displayDownload && styles.downloadExcel.box.open)}}>
                                    <div style={styles.downloadExcel.row}>
                                        <span>{"From"}</span>
                                        <input
                                            type="date"
                                            id="fromDate"
                                            name="downloadFromDate"
                                            value={downloadFromDate}
                                            onChange={(e) => setDownloadFromDate(e.target.value)}
                                            style={styles.downloadExcel.dateInput}
                                        />
                                    </div>
                                    <div style={styles.downloadExcel.row}>
                                        <span>{"To"}</span>
                                        <input
                                            type="date"
                                            id="toDate"
                                            name="downloadToDate"
                                            value={downloadToDate}
                                            onChange={(e) => setDownloadToDate(e.target.value)}
                                            style={styles.downloadExcel.dateInput}
                                        />
                                    </div>
                                    <Button
                                        style={styles.downloadExcel.downloadButton}
                                        onClick={downloadCVLessExcelSheet}
                                        disabled={downloadFromDate === "" || downloadToDate === ""}>{"Download"}
                                    </Button>
                                </div>
                            </div>
                        </div>
                        {isFilterPanelOpen && (
                            <RecruiterFilterPanel
                                styles={styles.filterPanel}
                                sectors={sectors}
                                jobTypes={jobTypes}
                                sectorId={sectorIds}
                                jobTypeId={jobTypeIds}
                                applicationType={applicationType}
                                hasSubOrganizations={hasSubOrganizations}
                                orgSelectInputs={orgSelectInputs}
                                onSectorChange={selectSector}
                                onJobTypeChange={selectJobType}
                                onApplicationTypeChange={setApplicationType}
                                onOrganizationChange={setOrganization}
                                onClearAll={clearAllFilters}
                                onApply={() => setIsFilterPanelOpen(false)}
                            />
                        )}
                        {/* Bulk action bar */}
                        {Object.keys(selectedVacancies).length > 0 && (
                            <div style={styles.bulkActionBar}>
                                <span style={styles.bulkActionBar.selected}>
                                    {Object.keys(selectedVacancies).length + " selected"}
                                </span>
                                <div style={styles.bulkActionBar.actions}>
                                    {hasExportableSelectedVacancy() && ( 
                                        <Button style={styles.bulkActionBar.exportButton} onClick={handleBulkExport}>
                                            <Excel style={styles.bulkActionBar.buttonIcon}/>{"Export"}
                                        </Button>
                                    )}
                                    {status !== "ALL" && status !== "ARCHIVE" && (
                                        <Button style={styles.bulkActionBar.archiveButton} onClick={() => {
                                            const jobIds = Object.keys(selectedVacancies).map(k => +k);
                                            if (jobIds.length > 0) {
                                                displayMultipleArchiveWarning(jobIds);
                                            }
                                        }}>
                                            <EyeClose style={styles.bulkActionBar.buttonIcon}/>{"Archive"}
                                        </Button>
                                    )}
                                    <Button style={styles.bulkActionBar.clearButton} onClick={() => setSelectedVacancies({})}>
                                        <CloseCircle style={styles.bulkActionBar.buttonIcon}/>{"Clear"}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>

                    <div style={{...styles.vacancyList, ...(!loading && vacancies.length === 0 && styles.vacancyList.noVacancies)}}>                            
                        {loading ? <VacancyListLoader/> :
                        <>                                
                            {vacancies.length > 0 ? <>
                                {vacancies.map((vacancy, index) =>
                                    <div
                                        key={index}
                                        style={{
                                            ...styles.vacancyCard,
                                            ...(index !== vacancies.length - 1 && styles.vacancyCard.bottomBorder),
                                            ...(selectedVacancies[vacancy.jobId] !== undefined && styles.vacancyCard.checked),
                                            ...(hoveredIndex === index && styles.vacancyCard.hovered),
                                            ...(index === 0 && styles.vacancyCard.firstItem),
                                            ...(index === vacancies.length - 1 && styles.vacancyCard.lastItem)
                                        }}
                                        onMouseEnter={() => setHoveredIndex(index)}
                                        onMouseLeave={() => setHoveredIndex(-1)}
                                        onClick={() => navigateToApplicationStatus(vacancy.jobId, vacancy.jobTitle)}
                                    >
                                        {/* Checkbox + main content */}
                                        <div style={styles.vacancyCard.titleRow}>
                                            <div style={styles.vacancyCard.option} onClick={(e) => toggleCheckbox(e, vacancy)}>
                                                {selectedVacancies[vacancy.jobId] !== undefined
                                                    ? <CheckMark style={styles.vacancyCard.checkMark} />
                                                    : <div style={styles.vacancyCard.checkbox}></div>}
                                            </div>
                                            <div style={styles.vacancyCard.rowMain}>
                                                <div style={styles.vacancyCard.titleLine}>
                                                    <div key={vacancy.jobId} style={styles.vacancyCard.titleContainer}>
                                                        <div
                                                            ref={(e) => (titleRefs.current[index] = e)}
                                                            style={{
                                                                ...styles.vacancyCard.title,
                                                                ...(hoveredIndex === index && handleTitleScroll(index) && styles.vacancyCard.title.slider)
                                                            }}
                                                        >
                                                            {vacancy.jobTitle}
                                                        </div>
                                                    </div>
                                                    {vacancy.isPreApplicationFormEnabled && <span style={styles.vacancyCard.tagOpt}>{"CV-Less"}</span>}
                                                    {vacancy.urgent && <span style={styles.vacancyCard.urgentTag}>{"Urgent"}</span>}
                                                    {vacancy.bumpUp && <div style={styles.vacancyCard.bumpUp}><BumpUp style={styles.vacancyCard.bumpUpIcon}/></div>}
                                                    {!isMobile && vacancy.published && 
                                                        <span 
                                                            style={{...styles.vacancyCard.promoteAd, ...(hoveredIndex === index && styles.vacancyCard.promoteAd.hovered)}}
                                                            onClick={(e) => toPromoteAd(e, vacancy.jobId)}
                                                        >
                                                            {"Promote"}
                                                        </span>
                                                    }
                                                </div>
                                                <div style={styles.vacancyCard.rowMeta}>
                                                    <Location style={styles.vacancyCard.location} />
                                                    <span>{formatLocations(vacancy.locations)}</span>
                                                    {vacancy.jobType && <span>{"·"}</span>}
                                                    <span>{vacancy.jobType}</span>
                                                    {vacancy.remote && <><span>{"·"}</span><span>{"Remote"}</span></>}
                                                </div>
                                            </div>
                                        </div>
                                        {/* Applicants + status/date */}
                                        <div style={styles.vacancyCard.rowRight}>
                                            <div style={styles.vacancyCard.applicants}>
                                                {vacancy.totalApplications + (vacancy.totalApplications === 1 ? " applicant" : " applicants")}
                                                {vacancy.unreadApplications > 0 &&
                                                    <span style={styles.vacancyCard.newBadge}>{" · " + vacancy.unreadApplications + " new"}</span>}
                                            </div>
                                            <div style={styles.vacancyCard.rowStatus}>
                                                {getStatusText(vacancy)}{" · "}{formatDateWithSuperscript(vacancy.createdDate)}
                                                {isMobile && vacancy.published && <span style={styles.vacancyCard.promoteAd} onClick={(e) => toPromoteAd(e, vacancy.jobId)}>{"Promote"}</span>} 
                                                {vacancy.isPreApplicationFormEnabled && <Excel style={styles.vacancyCard.downloadCvLess} onClick={(e) => addToExcelCvLess(e,vacancy.jobId)}/>}
                                            </div>
                                        </div>
                                        {/* Mobile action buttons */}
                                        {isMobile && <div style={styles.vacancyCard.buttons} onClick={(e) => e.stopPropagation()}>
                                            <div style={styles.vacancyCard.buttonWrapper}>
                                                <Button style={{...styles.vacancyCard.button, ...styles.vacancyCard.edit, ...(vacancy.confirmationPending && styles.vacancyCard.button.wider)}} onClick={() => editVacancy(vacancy.jobId)}>
                                                    <Edit style={styles.vacancyCard.editIcon}/>
                                                </Button>
                                                {!vacancy.confirmationPending && <Button style={{...styles.vacancyCard.button, ...styles.vacancyCard.duplicate}} onClick={() => displayDuplicateWarning(vacancy.jobId, vacancy.jobTitle)}>
                                                    <Duplicate style={styles.vacancyCard.duplicateIcon}/>
                                                </Button>}
                                            </div>
                                            <div style={styles.vacancyCard.buttonWrapper}>
                                                <Button style={{...styles.vacancyCard.button, ...styles.vacancyCard.view, ...((vacancy.archived || vacancy.confirmationPending) && styles.vacancyCard.button.wider)}} onClick={() => previewVacancy(vacancy.jobId)}>
                                                    <EyeOpen style={styles.vacancyCard.viewIcon}/>
                                                </Button>
                                                {vacancy.published && <Button style={styles.vacancyCard.archiveButton} onClick={() => displayArchiveWarning(vacancy.jobId, vacancy.jobTitle)}>{"Archive"}</Button>}
                                            </div>
                                        </div>}
                                        {/* Desktop: ⋮ menu button */}
                                        {!isMobile && (
                                            <button
                                                style={styles.vacancyCard.menuBtn}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setOpenMenuJobId(openMenuJobId === vacancy.jobId ? null : vacancy.jobId);
                                                }}
                                            >
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                                    <circle cx="12" cy="5" r="1.6"/>
                                                    <circle cx="12" cy="12" r="1.6"/>
                                                    <circle cx="12" cy="19" r="1.6"/>
                                                </svg>
                                            </button>
                                        )}
                                        {/* Desktop: dropdown menu */}
                                        {!isMobile && openMenuJobId === vacancy.jobId && (
                                            <div style={styles.vacancyCard.menu} onClick={(e) => e.stopPropagation()}>
                                                <button style={styles.vacancyCard.menuItem} onClick={() => { editVacancy(vacancy.jobId); setOpenMenuJobId(null); }}>
                                                    <Edit style={styles.vacancyCard.menuIcon}/>{"Edit job"}
                                                </button>
                                                {!vacancy.confirmationPending && (
                                                    <button style={styles.vacancyCard.menuItem} onClick={() => { displayDuplicateWarning(vacancy.jobId, vacancy.jobTitle); setOpenMenuJobId(null); }}>
                                                        <Duplicate style={styles.vacancyCard.menuIcon}/>{"Duplicate"}
                                                    </button>
                                                )}
                                                <button style={styles.vacancyCard.menuItem} onClick={() => { previewVacancy(vacancy.jobId); setOpenMenuJobId(null); }}>
                                                    <EyeOpen style={styles.vacancyCard.menuIcon}/>{"Preview"}
                                                </button>
                                                {vacancy.isPreApplicationFormEnabled && (
                                                    <button style={styles.vacancyCard.menuItem} onClick={(e) => { addToExcelCvLess(e, vacancy.jobId); setOpenMenuJobId(null); }}>
                                                        <Excel style={styles.vacancyCard.menuIcon}/>{"Export applicants"}
                                                    </button>
                                                )}
                                                {vacancy.published && (
                                                    <>
                                                        <div style={styles.vacancyCard.menuSep}/>
                                                        <button style={styles.vacancyCard.menuItemDanger} onClick={() => { displayArchiveWarning(vacancy.jobId, vacancy.jobTitle); setOpenMenuJobId(null); }}>
                                                            <EyeClose style={styles.vacancyCard.menuIcon}/>{"Archive"}
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </> : <FeedbackCard
                                    styles={styles.noVacancies}
                                    message={`There are no jobs available in ${status.toLowerCase()} status.`}
                                />
                            }
                        </>}
                    </div>
                    {!loading && hasExcessVacancies && (
                        <div style={styles.paginationWrapper}>
                            <SingleSelect 
                                styles={{
                                    ...styles.perPageSelect,
                                    ...(isMobile && totalPages! > 1 && { label: { width: "18vw" } })
                                }} 
                                label={"Posts Per Page"} 
                                selectInputs={perPageSelectInputs} 
                                getSelected={setPageSize} 
                            />
                            {(totalPages! > 1) && <Pagination page={page} totalPages={totalPages!} handlePagination={handlePagination} extendedStyles={styles.pagination} />}
                        </div>
                    )}
                </div>
            </div>
            {openVerifyDialog && <div style={styles.verifyDialog}>
                <div style={styles.verifyDialog.background} onClick={handleOpenVerifyDialog}></div>
                <div style={styles.verifyDialog.container}>
                    <CloseCircle style={styles.verifyDialog.close} onClick={handleOpenVerifyDialog}/>
                    <div style={styles.verifyDialog.text}>{Texts.verifyEmail}</div>
                </div>
            </div>}  
            <PopUpWindow styles={styles.warningDialog} open={openMessageDialog} toggle={closeMessageDialog} closeCircle={true}>
                {messageDialogContent}
            </PopUpWindow>
            {pendingInvoice && <PopUpWindow styles={styles.invoiceDialog} open={openInvoiceDialog} toggle={closeInvoiceDialog} closeCircle={true}>
                <WarningOrange style={styles.invoiceDialog.icon}/>
                <div style={styles.invoiceDialog.content}>
                    <p style={styles.invoiceDialog.message}>{"You have pending invoice payment"}</p>
                    <div style={styles.invoiceDialog.buttons}>                                  
                        <Button style={styles.invoiceDialog.button} onClick={handlePayInvoiceClick}>{"PAY NOW"}</Button>
                    </div>
                </div>
            </PopUpWindow>}
            <PopUpWindow styles={styles.warningDialog} open={openQuotaDialog} toggle={handleToggleQuotaDialog} closeCircle={true}>
                {quotaMessageContent}
            </PopUpWindow>
            <Alert/>      
            <Footer/>
        </>
    )
}